/**
 * The start page's calls: an applicant proves the email address they applied
 * with before any session exists.
 *
 * Every route here is public: no session token, no operator sign-in, no
 * cookie. That is why these do not go through `request` in `api.ts`, which
 * attaches a signed-in operator's access token and staff org selection to
 * every call. A person reading their own Application Received email on a
 * shared computer must not send someone else's operator sign-in along.
 *
 * Errors carry `{ error, code }`. The page picks its copy by `code`
 * (`startCopy.ts`), never from the `error` text.
 */
import { API_BASE_URL } from "@/lib/env";
import { apiErrorFrom, safeJson, type ApiError } from "@/lib/api";

/** The sign-ins that prove an email address. The start page offers no others. */
export type StartProvider = "google" | "linkedin" | "apple" | "microsoft";

export const START_PROVIDERS: readonly StartProvider[] = ["google", "linkedin", "apple", "microsoft"];

export interface StartContext {
  employer: { name: string };
  /** Null unless the link's job is one this employer has a title for. */
  job: { title: string } | null;
  /** The sign-ins configured here, in the order to show them. */
  providers: StartProvider[];
  /** Always null for now. A bot check widget mounts here when it is set. */
  turnstile: { site_key: string } | null;
}

export interface StartCodeSent {
  proof_id: string;
  expires_in_seconds: number;
  /** Test environments only, where no email is sent. */
  code?: string;
}

export type StartOutcome =
  | { outcome: "no_match" }
  | { outcome: "matched"; employer: string; job_title: string; continue_url: string };

async function startRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  if (!API_BASE_URL) {
    const err: ApiError = new Error("VITE_API_BASE_URL is not configured.");
    err.status = 0;
    throw err;
  }
  const hasBody = init.body != null;
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    credentials: "omit",
    cache: "no-store",
    headers: {
      ...(hasBody ? { "Content-Type": "application/json" } : {}),
      Accept: "application/json",
    },
  });
  const text = await res.text();
  const payload = text ? safeJson(text) : null;
  if (!res.ok) throw apiErrorFrom(res.status, payload);
  return payload as T;
}

const post = <T>(path: string, body: Record<string, unknown>) =>
  startRequest<T>(path, { method: "POST", body: JSON.stringify(body) });

/** Drops absent optional fields so the body carries only what was given. */
function withOptional(
  body: Record<string, unknown>,
  optional: Record<string, string | null | undefined>,
): Record<string, unknown> {
  const out = { ...body };
  for (const [k, v] of Object.entries(optional)) {
    if (v) out[k] = v;
  }
  return out;
}

export const startApi = {
  getContext: (org: string, job?: string | null) => {
    const q = new URLSearchParams({ org });
    if (job) q.set("job", job);
    return startRequest<StartContext>(`/api/v1/start/context?${q.toString()}`);
  },

  sendCode: (org: string, email: string, job?: string | null, turnstileToken?: string | null) =>
    post<StartCodeSent>(
      "/api/v1/start/code",
      withOptional({ org, email }, { job, turnstile_token: turnstileToken }),
    ),

  /** A wrong code is `200 { verified: false }`, not an error. */
  confirmCode: (org: string, proofId: string, code: string) =>
    post<{ verified: boolean }>("/api/v1/start/code/confirm", { org, proof_id: proofId, code }),

  startOAuth: (org: string, provider: StartProvider, job?: string | null, turnstileToken?: string | null) =>
    post<{ redirect_url: string }>(
      `/api/v1/start/oauth/${encodeURIComponent(provider)}`,
      withOptional({ org }, { job, turnstile_token: turnstileToken }),
    ),

  continueWithProof: (org: string, proofId: string) =>
    post<StartOutcome>("/api/v1/start/continue", { org, proof_id: proofId }),
};
